import jwt_decode from 'jwt-decode';
import { RootState } from './store';
import Cookies from 'js-cookie';

const transparencyCatalogDomain = 'transparencycatalog.com';

export const loadState = () => {
    try {
        const serializedState = localStorage.getItem('state');
        if (!serializedState) {
            return undefined;
        }
        return JSON.parse(serializedState);
    } catch (error) {
        return undefined;
    }
};

export const saveState = (state: RootState) => {
    try {
        const serializedState = JSON.stringify(state);
        localStorage.setItem('state', serializedState);
    } catch (error) {}
};

export const clearState = () => {
    localStorage.clear();
    Cookies.remove('SmSession', {
        domain: transparencyCatalogDomain,
    });
    Cookies.remove('SmRefreshSession', {
        domain: transparencyCatalogDomain,
    });
    Cookies.remove('pbUserAccountType', {
        domain: transparencyCatalogDomain,
    });
    Cookies.remove('pbIsLiveGuideline', {
        domain: transparencyCatalogDomain,
    });
};

export const setCookies = (accessToken: string, refreshToken: string) => {
    Cookies.set('SmSession', accessToken, {
        domain: transparencyCatalogDomain,
    });
    Cookies.set('SmRefreshSession', refreshToken, {
        domain: transparencyCatalogDomain,
    });
};

export const setAccountTypeCookie = (accountType: string) => {
    Cookies.set('pbUserAccountType', accountType, {
        domain: transparencyCatalogDomain,
    });
};

// Tells TC the search page was opened to create/edit a Live Guideline; must be reset to false for every other TC redirect.
export const setLiveGuidelineCookie = (isLiveGuideline: boolean) => {
    Cookies.set('pbIsLiveGuideline', String(isLiveGuideline), {
        domain: transparencyCatalogDomain,
    });
};

const getTokenExp = (token: string | undefined | null): number => {
    if (!token) {
        return 0;
    }
    try {
        const decoded = jwt_decode(token) as { exp?: number };
        return decoded.exp ?? 0;
    } catch {
        return 0;
    }
};

/**
 * Keeps localStorage and shared-domain cookies aligned, preferring whichever access token expires later.
 * Bug 892: API reads localStorage while Transparency Catalog uses cookies; stale tokens caused
 * product list to revert until cookies were cleared.
 */
export const syncSessionTokensFromCookiesAndLocalStorage = (): void => {
    const cookieAccess = Cookies.get('SmSession');
    const localAccess = localStorage.getItem('SmSession');
    const cookieRefresh = Cookies.get('SmRefreshSession');
    const localRefresh = localStorage.getItem('SmRefreshSession');

    const cookieAccessExp = getTokenExp(cookieAccess);
    const localAccessExp = getTokenExp(localAccess);

    const useCookies = cookieAccessExp >= localAccessExp && !!cookieAccess;

    const accessToken = useCookies ? cookieAccess! : localAccess || cookieAccess || '';
    const refreshToken = useCookies
        ? cookieRefresh || localRefresh || ''
        : localRefresh || cookieRefresh || '';

    if (accessToken) {
        localStorage.setItem('SmSession', accessToken);
        localStorage.setItem('Expired', String(getTokenExp(accessToken)));
    }
    if (refreshToken) {
        localStorage.setItem('SmRefreshSession', refreshToken);
    }
    if (accessToken && refreshToken) {
        setCookies(accessToken, refreshToken);
    }
};

export const checkCookies = () => {
    syncSessionTokensFromCookiesAndLocalStorage();
};

export const getAccessToken = (): string => {
    const accessToken = localStorage.getItem('SmSession');
    return accessToken || '';
};

export const getRefreshToken = (): string => {
    const refreshToken = localStorage.getItem('SmRefreshSession');
    return refreshToken || '';
};

/** @deprecated Use syncSessionTokensFromCookiesAndLocalStorage */
export const copyTokensFromCookiesToLocalStorage = () => {
    syncSessionTokensFromCookiesAndLocalStorage();
};
